import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

const SLIDES = [
  {
    eyebrow: "Une école au service de ses étudiants",
    title: ["Développez votre", "employabilité"],
    italicWord: "employabilité",
    desc:
      "Une alliance école-entreprise ancrée dans des valeurs humaines. Pour renforcer votre employabilité, vous accompagner pendant vos études et faciliter votre entrée dans la vie active.",
    ctaLabel: "En savoir plus",
    ctaTarget: "/pourquoi-nous",
    image:
      "/assets/etudiants-campus-2.jpg",
  },
  {
    eyebrow: "Mot du Président",
    title: ["Former et transmettre", "pour un avenir plus juste."],
    italicWord: "transmettre",
    desc:
      "« Bâtir un avenir plus juste pour notre jeunesse. »\nHervé Diaz — Président de l'École de Commerce de Lyon.",
    ctaLabel: "Lire le mot du Président",
    ctaTarget: "/#mot-president",
    scrollTo: "#mot-president",
    image:
      "/assets/campus-interieur.jpg",
  },
  {
    eyebrow: "École de Commerce de Lyon",
    title: ["Préparez votre", "avenir."],
    italicWord: "avenir",
    desc:
      "Imaginez un avenir où votre carrière a du sens. Le groupe ECL est une communauté mondiale animée par une raison d'être simple et forte : avoir un impact positif sur le monde en réinventant la façon de produire, d'échanger et d'entreprendre.",
    ctaLabel: "Nos formations",
    ctaTarget: "/formations",
    image:
      "/assets/business-handshake.jpg",
  },
];

const SLIDE_DURATION = 7000;

export const HeroCarousel = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const goTo = useCallback((i) => {
    setIndex(((i % SLIDES.length) + SLIDES.length) % SLIDES.length);
  }, []);

  const next = useCallback(() => goTo(index + 1), [index, goTo]);
  const prev = useCallback(() => goTo(index - 1), [index, goTo]);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setTimeout(() => next(), SLIDE_DURATION);
    return () => clearTimeout(timerRef.current);
  }, [index, paused, next]);

  const handleCta = (slide) => {
    if (slide.scrollTo) {
      const el = document.querySelector(slide.scrollTo);
      if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
    }
    navigate(slide.ctaTarget);
  };

  const slide = SLIDES[index];

  return (
    <section
      id="hero"
      data-testid="hero-carousel"
      className="relative min-h-screen w-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides */}
      <AnimatePresence initial={false} mode="sync">
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
          aria-hidden="true"
        >
          <img src={slide.image} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 hero-overlay pointer-events-none" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="container-edge relative z-10 min-h-screen flex items-center pt-28 pb-32 md:pt-32 md:pb-24">
        <div className="max-w-4xl w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              data-testid={`hero-slide-${index}`}
            >
              <div className="overline mb-6 text-white/70" style={{ color: "rgba(255,255,255,0.7)" }}>
                {slide.eyebrow}
              </div>

              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium text-white leading-[1.05] tracking-tight" data-testid="hero-carousel-title">
                {slide.title[0]}{" "}
                <em className="not-italic shine-light">{slide.title[1]}</em>
              </h1>

              <p className="mt-8 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
                {slide.desc}
              </p>

              <div className="mt-12 flex flex-wrap gap-4">
                <button
                  onClick={() => handleCta(slide)}
                  className="btn-red"
                  data-testid={`hero-slide-cta-${index}`}
                >
                  {slide.ctaLabel}
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => navigate("/contact#candidater")}
                  className="btn-outline-light"
                  data-testid="hero-carousel-candidater"
                >
                  Candidater
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-6 md:bottom-10 left-0 right-0 z-20">
        <div className="container-edge flex items-center justify-between gap-4">
          {/* Dots + slide counter */}
          <div className="flex items-center gap-3 md:gap-6">
            <div className="flex items-center gap-2">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  data-testid={`hero-dot-${i}`}
                  aria-label={`Aller au slide ${i + 1}`}
                  className="relative h-1 transition-all duration-500 hover:opacity-100"
                  style={{
                    width: i === index ? 42 : 20,
                    background: i === index ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.25)",
                  }}
                >
                  {i === index && !paused && (
                    <motion.span
                      key={`fill-${index}`}
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: SLIDE_DURATION / 1000, ease: "linear" }}
                      className="absolute inset-0 bg-white"
                    />
                  )}
                  {i === index && paused && (
                    <span className="absolute inset-0 bg-white" />
                  )}
                </button>
              ))}
            </div>
            <span className="text-white/70 text-[10px] md:text-[11px] uppercase tracking-[0.22em] font-mono whitespace-nowrap">
              {String(index + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
            </span>
          </div>

          {/* Arrows */}
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              data-testid="hero-prev"
              aria-label="Slide précédent"
              className="w-10 h-10 md:w-12 md:h-12 border border-white/30 text-white hover:bg-white hover:text-navy-700 transition-all flex items-center justify-center"
            >
              <ChevronLeft className="h-4 w-4 md:h-5 md:w-5" />
            </button>
            <button
              onClick={next}
              data-testid="hero-next"
              aria-label="Slide suivant"
              className="w-10 h-10 md:w-12 md:h-12 border border-white/30 text-white hover:bg-white hover:text-navy-700 transition-all flex items-center justify-center"
            >
              <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: "smooth" })}
        className="absolute bottom-32 right-6 lg:right-12 z-10 text-white/60 hover:text-white transition-colors hidden lg:flex flex-col items-center gap-2"
        aria-label="Scroll"
        data-testid="hero-scroll-indicator"
      >
        <span className="overline text-white/50">Scroll</span>
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </button>
    </section>
  );
};

export default HeroCarousel;
